import { Building2, MapPin, ExternalLink, Clock, CheckCircle2, FolderOpen, Lock, Mail } from 'lucide-react';
import type { AuditorCompany } from '../../types/auditor';
import type { CreditScoreStats } from '../../services/api/endpoints/creditScore';
import { CreditScoreBadge } from './CreditScoreBadge';
import { getInitials } from '../../utils/creditScoreHelpers';

interface AuditorCompanyRowProps {
  company: AuditorCompany;
  isRecentlyOpened?: boolean;
  onOpen: (company: AuditorCompany) => void;
  creditScore?: CreditScoreStats | null;
  onCetClick?: (company: AuditorCompany) => void;
}

export function AuditorCompanyRow({
  company,
  isRecentlyOpened = false,
  onOpen,
  creditScore,
  onCetClick,
}: AuditorCompanyRowProps) {
  const { organization: org, fiscalYearsSummary: summary, membership } = company;
  const initials = getInitials(org.name);

  return (
    <tr
      className="group hover:bg-muted/50 transition-colors cursor-pointer border-b border-border last:border-0"
      onClick={() => onOpen(company)}
    >
      {/* Logo + Nom */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            {org.logo ? (
              <img
                src={org.logo}
                alt={org.name}
                className="h-9 w-9 rounded-lg object-contain border border-border bg-muted"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div className="h-9 w-9 rounded-lg flex items-center justify-center text-sm font-bold bg-primary/10 text-primary border border-primary/20 select-none">
                {initials || <Building2 className="h-4 w-4" />}
              </div>
            )}
          </div>
          <div className="min-w-0">
            <div className="font-medium text-foreground truncate group-hover:text-primary transition-colors">
              {org.name}
            </div>
            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
              <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                {org.legalForm}
              </span>
              {org.accountingMode && (
                <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                  {org.accountingMode}
                </span>
              )}
              {isRecentlyOpened && (
                <span className="inline-flex items-center gap-1 text-xs text-primary">
                  <Clock className="h-3 w-3" /> Récent
                </span>
              )}
            </div>
            {/* Extra info visible only on mobile where table columns are hidden */}
            <div className="mt-1 flex flex-col gap-0.5 md:hidden">
              {org.registrationNumber && (
                <span className="text-xs text-muted-foreground">RCCM: <span className="font-mono">{org.registrationNumber}</span></span>
              )}
              {(org.city || org.country) && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3 shrink-0" />
                  {[org.city, org.country].filter(Boolean).join(', ')}
                </span>
              )}
            </div>
          </div>
        </div>
      </td>

      {/* RCCM */}
      <td className="px-4 py-3 text-sm font-mono text-foreground/80 hidden md:table-cell">
        {org.registrationNumber || '—'}
      </td>

      {/* NIF */}
      <td className="px-4 py-3 text-sm font-mono text-foreground/80 hidden md:table-cell">
        {org.taxId || '—'}
      </td>

      {/* Devise */}
      <td className="px-4 py-3 text-sm text-foreground/80 hidden lg:table-cell">
        {org.currency || '—'}
      </td>

      {/* Localisation + Email */}
      <td className="px-4 py-3 hidden lg:table-cell">
        <div className="space-y-0.5">
          {(org.city || org.country) ? (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{[org.city, org.country].filter(Boolean).join(', ')}</span>
            </div>
          ) : null}
          {org.email && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Mail className="h-3 w-3 shrink-0" />
              <span className="truncate">{org.email}</span>
            </div>
          )}
        </div>
      </td>

      {/* Exercices */}
      <td className="px-4 py-3 hidden xl:table-cell">
        <div className="flex items-center gap-1.5">
          {summary.open > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
              <FolderOpen className="h-3 w-3" />{summary.open}
            </span>
          )}
          {summary.closed > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              <Lock className="h-3 w-3" />{summary.closed}
            </span>
          )}
          {summary.audited > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
              <CheckCircle2 className="h-3 w-3" />{summary.audited}
            </span>
          )}
        </div>
      </td>

      {/* Membre depuis */}
      <td className="px-4 py-3 hidden xl:table-cell">
        <div className="text-xs text-muted-foreground">
          {new Date(membership.addedAt).toLocaleDateString('fr-FR')}
        </div>
        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
          membership.status === 'active'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        }`}>
          {membership.status === 'active' ? 'Actif' : 'Inactif'}
        </span>
      </td>

      {/* Credit Score + CET */}
      <td className="px-4 py-3">
        <CreditScoreBadge
          creditScore={creditScore}
          company={company}
          onCetClick={onCetClick}
          compact
        />
      </td>

      {/* Action */}
      <td className="px-4 py-3">
        <button
          className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-sm font-medium
            bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground
            transition-colors duration-200"
          onClick={(e) => { e.stopPropagation(); onOpen(company); }}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Ouvrir</span>
        </button>
      </td>
    </tr>
  );
}
